import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Input, Popover, TextField } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { formatImportMoney, variantPickerLabel } from '../manualImportMeta';
import type { useProductPicker } from './useProductPicker';

type ProductPickerViewProps = {
    picker: ReturnType<typeof useProductPicker>;
};

/**
 * Render-only "Add product" overlay: search the catalog, then pick a variant.
 * Built on HeroUI `Popover` so it portals out of the card (never clipped) and
 * dismisses on outside click / Escape. All state + data live in
 * {@link useProductPicker}; the container (`ProductPicker`) wires it in.
 */
export const ProductPickerView: React.FC<ProductPickerViewProps> = ({
    picker,
}) => {
    const { t } = useTranslation('pages');

    return (
        <Popover isOpen={picker.isOpen} onOpenChange={picker.onOpenChange}>
            <Button
                size="sm"
                variant="secondary"
                {...getNodeTestIdProp('manual-import-add-product')}
            >
                <Icon icon="solar:magnifer-bold-duotone" />
                {t('purchases.manualImport.addProduct')}
            </Button>
            <Popover.Content
                className="w-80"
                {...getNodeTestIdProp('manual-import-picker')}
            >
                <Popover.Dialog
                    className="flex flex-col gap-2"
                    {...getNodeTestIdProp('manual-import-picker-dialog')}
                >
                    {picker.productId === null ? (
                        <>
                            <TextField
                                aria-label={t(
                                    'purchases.manualImport.searchProducts',
                                )}
                                autoFocus
                                fullWidth
                                onChange={picker.onSearchChange}
                                value={picker.search}
                                {...getNodeTestIdProp(
                                    'manual-import-picker-search-field',
                                )}
                            >
                                <Input
                                    placeholder={t(
                                        'purchases.manualImport.searchProducts',
                                    )}
                                    variant="secondary"
                                    {...getNodeTestIdProp(
                                        'manual-import-picker-search',
                                    )}
                                />
                            </TextField>
                            <ul
                                className="max-h-64 overflow-auto"
                                {...getNodeTestIdProp(
                                    'manual-import-picker-results',
                                )}
                            >
                                {picker.products.length === 0 ? (
                                    <li
                                        className="px-2 py-3 text-center text-sm text-muted"
                                        {...getNodeTestIdProp(
                                            'manual-import-picker-empty',
                                        )}
                                    >
                                        {t('purchases.manualImport.noResults')}
                                    </li>
                                ) : (
                                    picker.products.map((product) => (
                                        <li
                                            key={product.id}
                                            {...getNodeTestIdProp(
                                                `manual-import-picker-result-${product.id}`,
                                            )}
                                        >
                                            <button
                                                className="flex w-full flex-col items-start rounded-lg px-2 py-2 text-left hover:bg-surface-secondary"
                                                onClick={() =>
                                                    picker.onSelectProduct(
                                                        product.id,
                                                    )
                                                }
                                                type="button"
                                                {...getNodeTestIdProp(
                                                    `manual-import-picker-product-${product.id}`,
                                                )}
                                            >
                                                <span className="text-sm text-foreground">
                                                    {product.name}
                                                </span>
                                                {product.category ? (
                                                    <span className="text-xs text-muted">
                                                        {product.category.name}
                                                    </span>
                                                ) : null}
                                            </button>
                                        </li>
                                    ))
                                )}
                            </ul>
                        </>
                    ) : (
                        <>
                            <button
                                className="flex w-fit items-center gap-1 text-xs text-muted hover:text-foreground"
                                onClick={picker.onBackToSearch}
                                type="button"
                                {...getNodeTestIdProp(
                                    'manual-import-picker-back',
                                )}
                            >
                                <Icon icon="gravity-ui:arrow-left" />
                                {t('purchases.manualImport.backToSearch')}
                            </button>
                            <ul
                                className="max-h-64 overflow-auto"
                                {...getNodeTestIdProp(
                                    'manual-import-picker-variants',
                                )}
                            >
                                {picker.variants.map((variant) => (
                                    <li
                                        key={variant.id}
                                        {...getNodeTestIdProp(
                                            `manual-import-picker-variant-item-${variant.id}`,
                                        )}
                                    >
                                        <button
                                            className="flex w-full items-center justify-between rounded-lg px-2 py-2 text-left hover:bg-surface-secondary"
                                            onClick={() =>
                                                picker.onPickVariant(variant)
                                            }
                                            type="button"
                                            {...getNodeTestIdProp(
                                                `manual-import-picker-variant-${variant.id}`,
                                            )}
                                        >
                                            <span className="text-sm text-foreground">
                                                {variantPickerLabel(variant)}
                                            </span>
                                            <span className="text-xs text-muted">
                                                {formatImportMoney(
                                                    variant.salePrice,
                                                )}
                                            </span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </Popover.Dialog>
            </Popover.Content>
        </Popover>
    );
};
